const RAW_SITE_URL = process.env.REACT_APP_SITE_URL?.trim() || "https://buildwit.in";

export const SITE_URL = RAW_SITE_URL.replace(/\/$/, "");
export const COMPANY_NAME = "Buildwit Technology Pvt. Ltd.";
export const COMPANY_SHORT_NAME = "Buildwit Technology";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/Logonew.png`;

const COMPANY_DESCRIPTION =
  "Buildwit Technology is a Faridabad-based industrial automation manufacturer delivering precision machinery for government PSUs, defence, railways, pharma and heavy manufacturing across India.";

const ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Shop No. 19, H.B. Colony, Sector 22",
  addressLocality: "Faridabad",
  addressRegion: "Haryana",
  postalCode: "121005",
  addressCountry: "IN",
};

export const DEFAULT_SEO = {
  title: "Industrial Automation Manufacturer in India | Buildwit Technology",
  description: COMPANY_DESCRIPTION,
  keywords: [
    "industrial automation manufacturer India",
    "industrial machinery manufacturer Faridabad",
    "government PSU machinery supplier",
    "defence automation systems India",
    "pharma machinery manufacturer India",
    "Buildwit Technology",
  ],
  image: DEFAULT_OG_IMAGE,
  type: "website",
};

const STATIC_PAGE_SEO = {
  "/": DEFAULT_SEO,
  "/about": {
    title: "About Buildwit Technology | Faridabad Industrial Automation Manufacturer",
    description:
      "Learn how Buildwit Technology designs, manufactures and commissions industrial automation machinery in Faridabad for government, defence, railways, pharma and heavy industry.",
    keywords: [
      "about Buildwit Technology",
      "Faridabad machinery manufacturer",
      "industrial engineering company India",
      "government automation partner India",
    ],
    image: `${SITE_URL}/director/director.png`,
    type: "website",
  },
  "/products": {
    title: "Industrial Machinery Portfolio | CNC, Packaging, Conveyors and Automation Cells",
    description:
      "Explore Buildwit Technology's industrial machinery portfolio including CNC machines, packaging lines, conveyor systems, material handling solutions and automation cells.",
    keywords: [
      "industrial machinery portfolio",
      "CNC machines India",
      "packaging line manufacturer",
      "conveyor systems manufacturer",
      "automation cells India",
    ],
    image: DEFAULT_OG_IMAGE,
    type: "website",
  },
  "/industries": {
    title: "Industries Served | Government, Defence, Railways, Pharma and Manufacturing",
    description:
      "Buildwit Technology supplies industrial automation systems for government procurement, railways, defence, pharma, food processing, packaging and heavy manufacturing sectors.",
    keywords: [
      "industries served industrial automation",
      "railway machinery manufacturer India",
      "defence machinery supplier India",
      "pharma automation systems India",
    ],
    image: DEFAULT_OG_IMAGE,
    type: "website",
  },
  "/contact": {
    title: "Contact Buildwit Technology | Industrial Machinery Enquiries",
    description:
      "Contact Buildwit Technology in Faridabad for industrial machinery enquiries, technical proposals, production requirements and project consultation.",
    keywords: [
      "contact Buildwit Technology",
      "industrial machinery enquiry India",
      "Faridabad automation company contact",
      "request machinery quote India",
    ],
    image: DEFAULT_OG_IMAGE,
    type: "website",
  },
  "/admin": {
    title: "Buildwit Admin",
    description: "Administrative area for Buildwit Technology.",
    keywords: [],
    image: DEFAULT_OG_IMAGE,
    type: "website",
  },
};

export function getStaticPageSeo(pathname) {
  return STATIC_PAGE_SEO[pathname] || DEFAULT_SEO;
}

export function normalizePath(pathname = "/") {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

export function buildCanonicalUrl(pathname = "/") {
  const normalizedPath = normalizePath(pathname);
  return normalizedPath === "/" ? `${SITE_URL}/` : `${SITE_URL}${normalizedPath}`;
}

export function toAbsoluteUrl(url) {
  if (!url) {
    return DEFAULT_OG_IMAGE;
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  return `${SITE_URL}${url.startsWith("/") ? url : `/${url}`}`;
}

function prettifySegment(segment) {
  return segment
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function truncateDescription(value, maxLength = 160) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }

  return `${normalized.slice(0, maxLength - 1).trimEnd()}...`;
}

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: COMPANY_NAME,
    alternateName: COMPANY_SHORT_NAME,
    url: `${SITE_URL}/`,
    logo: DEFAULT_OG_IMAGE,
    image: DEFAULT_OG_IMAGE,
    description: COMPANY_DESCRIPTION,
    email: "sales@buildwit.in",
    telephone: "+91 8178799057",
    address: ADDRESS,
    foundingDate: "2026",
    areaServed: "IN",
    knowsAbout: [
      "Industrial automation",
      "Precision machinery",
      "Government PSU manufacturing",
      "Defence engineering",
      "Railway automation",
      "Pharma machinery",
    ],
  };
}

export function buildLocalBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#localbusiness`,
    name: COMPANY_SHORT_NAME,
    url: `${SITE_URL}/`,
    image: DEFAULT_OG_IMAGE,
    email: "sales@buildwit.in",
    telephone: "+91 8178799057",
    address: ADDRESS,
    openingHours: "Mo-Sa 09:00-18:30",
    areaServed: {
      "@type": "Country",
      name: "India",
    },
    parentOrganization: {
      "@id": `${SITE_URL}/#organization`,
    },
  };
}

export function buildWebsiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: `${SITE_URL}/`,
    name: COMPANY_SHORT_NAME,
    description: COMPANY_DESCRIPTION,
    publisher: {
      "@id": `${SITE_URL}/#organization`,
    },
    inLanguage: "en-IN",
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/products?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

export function buildBreadcrumbSchema(pathname, pageName) {
  const normalizedPath = normalizePath(pathname);
  const segments = normalizedPath.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE_URL}/`,
    },
  ];

  let currentPath = "";
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    items.push({
      "@type": "ListItem",
      position: index + 2,
      name: index === segments.length - 1 && pageName ? pageName : prettifySegment(segment),
      item: buildCanonicalUrl(currentPath),
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

export function buildPageSchema({ pathname, title, description, pageType = "WebPage" }) {
  return {
    "@context": "https://schema.org",
    "@type": pageType,
    name: title,
    description,
    url: buildCanonicalUrl(pathname),
    inLanguage: "en-IN",
    isPartOf: {
      "@id": `${SITE_URL}/#website`,
    },
    about: {
      "@id": `${SITE_URL}/#organization`,
    },
  };
}

export function buildStaticPageSchemas(pathname, pageSeo) {
  const schemas = [
    buildOrganizationSchema(),
    buildWebsiteSchema(),
    buildBreadcrumbSchema(pathname, pageSeo.title),
  ];

  if (pathname === "/") {
    schemas.push(buildLocalBusinessSchema());
    schemas.push(buildPageSchema({ pathname, title: pageSeo.title, description: pageSeo.description }));
    return schemas;
  }

  if (pathname === "/about") {
    schemas.push(buildPageSchema({ pathname, title: pageSeo.title, description: pageSeo.description, pageType: "AboutPage" }));
    return schemas;
  }

  if (pathname === "/products") {
    schemas.push(buildPageSchema({ pathname, title: pageSeo.title, description: pageSeo.description, pageType: "CollectionPage" }));
    return schemas;
  }

  if (pathname === "/industries") {
    schemas.push(buildPageSchema({ pathname, title: pageSeo.title, description: pageSeo.description, pageType: "CollectionPage" }));
    return schemas;
  }

  if (pathname === "/contact") {
    schemas.push(buildLocalBusinessSchema());
    schemas.push(buildPageSchema({ pathname, title: pageSeo.title, description: pageSeo.description, pageType: "ContactPage" }));
    return schemas;
  }

  return schemas;
}

export function buildProductSchema(product) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: truncateDescription(product.description || product.tagline || DEFAULT_SEO.description, 300),
    sku: product.slug,
    category: prettifySegment(product.category_slug || "industrial-machinery"),
    url: buildCanonicalUrl(`/products/${product.slug}`),
    image: Array.isArray(product.images) ? product.images.map((image) => toAbsoluteUrl(image)) : [DEFAULT_OG_IMAGE],
    brand: {
      "@type": "Brand",
      name: COMPANY_SHORT_NAME,
    },
    manufacturer: {
      "@id": `${SITE_URL}/#organization`,
    },
    audience: Array.isArray(product.industries)
      ? product.industries.map((industry) => ({
          "@type": "Audience",
          audienceType: industry,
        }))
      : undefined,
    additionalProperty: Array.isArray(product.specifications)
      ? product.specifications.map((specification) => ({
          "@type": "PropertyValue",
          name: specification.key,
          value: specification.value,
        }))
      : undefined,
  };
}